import { Button, Icon } from '@idexx-laboratories/spot-react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import React, { useEffect } from 'react';
import 'react-datepicker/dist/react-datepicker.css';
import { useNavigate } from 'react-router';
import CalendarWithOffset from '../../components/CalendarWithOffset/CalendarWithOffset';
import Loader from '../../components/Loader/Loader';
import { useStore } from '../../hooks/use-store';
import './ScheduleUpgrade.scss';

dayjs.extend(utc);

const ScheduleUpgrade = observer(() => {
  const { scheduleUpgradeStore: store, globalStore, globalModalStore } = useStore();
  const {
    practice,
    sapId,
    saveLoading,
    utcOffset,
    dateStr,
    timeZoneName,
    continueDisabled,
    scheduleUpgradeTimestamp,
  } = store;
  const { eventIntervalsHandler } = globalStore;
  const navigate = useNavigate();

  useEffect(() => {
    store.init();
    return () => store.close();
  }, [navigate, sapId, store]);

  return (
    <div className='schedule-upgrade-wrapper'>
      <div className='spot-typography__heading--level-2'>
        Schedule Upgrade for{' '}
        <span title={globalStore.selectedSap.name}>{globalStore.selectedSap.id}&apos;s</span>{' '}
        {practice?.toCsVersion.version} upgrade
      </div>
      <div className='spot-typography__text--tertiary'>
        Select the date and time to start your automated upgrade.
      </div>
      <div className='schedule-upgrade_body'>
        <CalendarWithOffset
          type='Date'
          className='schedule-calendar'
          value={scheduleUpgradeTimestamp}
          onChange={timestamp => runInAction(() => (store.scheduleUpgradeTimestamp = timestamp))}
          eventIntervalsHandler={eventIntervalsHandler}
          utcOffset={utcOffset}
          data-testid='calendar-date'
        />
        <div className='start-time-wrapper'>
          <div className='start-time-label'>Start Time</div>
          <CalendarWithOffset
            type='Time'
            className='time-select'
            value={scheduleUpgradeTimestamp}
            onChange={timestamp => runInAction(() => (store.scheduleUpgradeTimestamp = timestamp))}
            utcOffset={utcOffset}
            eventIntervalsHandler={eventIntervalsHandler}
            data-testid='calendar-time'
          />
          <span className='start-time-timezone'>
            {timeZoneName}
            {!practice?.timeZoneName && (
              <Icon
                className='info-icon'
                iconName='info-2'
                title='The current time zone displayed on the web page reflects the last checked UTC offset and updates if any changes have occurred.'
              />
            )}
          </span>
        </div>
        <p>Scheduled</p>
        <p className='show-date' role='show-date'>
          {dateStr} {timeZoneName}
        </p>
        {practice && !practice.isLocked && (
          <div className='disclaimer'>
            <p>
              <Icon className='note-icon' iconName='alert-notification' title='expand' />
              Note
            </p>
            <p>
              Clicking <span className='bold'>Continue</span> will give you exclusive access to the
              upgrade task to prevent concurrent scheduling.
            </p>
            <p>
              We will prompt you at the end of <span className='bold'>90 minutes</span>, and you can
              extend your session if needed.
            </p>
          </div>
        )}
        <div className='page-bottom-actions'>
          <Button
            variant='link'
            size='small'
            onClick={() => globalModalStore.showExitUpgradePopup(() => store.exitUpgrade())}
            data-testid='exit-upgrade-btn'
          >
            Exit Upgrade
          </Button>
          <div className='main-buttons'>
            <Button
              className='schedule-btn'
              variant='primary'
              size='small'
              disabled={continueDisabled}
              onClick={() => void store.confirmSchedule()}
              data-testid='continue-btn'
            >
              <Loader loading={saveLoading}>Continue</Loader>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
});

export default ScheduleUpgrade;
